import { useMemo } from 'react';

import { Checkbox } from '@rocco/components/checkbox';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as CloseIcon } from '@rocco/icons/svg/close.svg';

import { SelectedItemMedia } from './SelectedItemMedia';
import { countLabel } from './bulk-enquiry-copy';
import type { BulkEnquiryItem } from './bulk-enquiry-state';

interface SelectedItemsListProps {
    items: BulkEnquiryItem[];
    onRemove: (siteTreeID: number) => void;
    /** Renders a clear-all footer. Omit when the consumer places its own
     * clear-all control (the island puts it beside its heading). */
    onClearAll?: () => void;
    /**
     * Group the rows under a heading per supplier. Mirrors how the enquiry is
     * actually sent — one per supplier, carrying only their items — so the
     * fan-out is obvious before submitting.
     */
    groupBySupplier?: boolean;
    /**
     * Show a ticked checkbox per row instead of the remove cross. Unticking
     * removes the item, so the gate modal's list and the island stay one
     * selection rather than two.
     */
    selectable?: boolean;
    /** Sent already — render the list as a record, with no controls. */
    readOnly?: boolean;
    className?: string;
    /**
     * Overrides the scroll box's default `max-h-[45vh]` cap — the popover and
     * review step want a bounded list, a full-height pane wants to fill.
     */
    scrollClassName?: string;
}

interface SupplierGroup {
    targetProfessionalId: number;
    supplierName?: string;
    items: BulkEnquiryItem[];
}

/** One selected item: thumbnail, title, a subtitle, and a remove control. */
const ItemRow = ({
    item,
    onRemove,
    showSupplier,
    selectable,
    readOnly,
}: {
    item: BulkEnquiryItem;
    onRemove: (siteTreeID: number) => void;
    showSupplier: boolean;
    selectable: boolean;
    readOnly: boolean;
}) => {
    // A professional's title IS their name, so repeating it as the supplier line
    // says nothing — show what they do instead. Products and projects name their
    // supplier, unless a group heading is already saying it.
    const subtitle =
        item.itemType === 'professional'
            ? item.category
            : showSupplier
              ? item.supplierName
              : undefined;

    // Both gate-pane modes share the larger type; only the island popover and
    // the legacy review step use the compact scale.
    const inGatePane = selectable || readOnly;
    const details = (
        <div className="min-w-0 flex-1">
            <p
                className={cn(
                    'line-clamp-1 font-medium text-[#181818]',
                    inGatePane ? 'text-[0.9375rem]' : 'text-sm',
                )}
            >
                {item.title}
            </p>
            {subtitle ? (
                <p
                    className={cn(
                        'line-clamp-1 text-gray-500',
                        inGatePane ? 'text-[0.8125rem]' : 'text-xs',
                    )}
                >
                    {subtitle}
                </p>
            ) : null}
        </div>
    );

    // Once sent there is nothing left to deselect, so the row loses its control
    // and becomes a plain record of what went out.
    if (readOnly) {
        return (
            <li className="flex items-center gap-x-3.5 py-2.5">
                <SelectedItemMedia
                    item={item}
                    className="size-[5.625rem] rounded-md"
                />
                {details}
            </li>
        );
    }

    // Every listed item is by definition selected, so the box is always ticked;
    // clearing it drops the item from the shared selection, which is what the
    // island shows too — there is no second notion of "selected" to diverge.
    if (selectable) {
        return (
            <li>
                <label className="flex cursor-pointer items-center gap-x-3.5 py-2.5">
                    <Checkbox
                        checked
                        onCheckedChange={() => onRemove(item.siteTreeID)}
                        aria-label={`Remove ${item.title} from your enquiry`}
                        className="size-[1.125rem] shrink-0"
                        indicatorClassName="size-3.5"
                    />
                    <SelectedItemMedia
                        item={item}
                        className="size-[5.625rem] rounded-md"
                    />
                    {details}
                </label>
            </li>
        );
    }

    return (
        <li className="flex items-center gap-x-3 py-2">
            <SelectedItemMedia item={item} className="size-14 rounded-md" />
            {details}
            <button
                type="button"
                onClick={() => onRemove(item.siteTreeID)}
                aria-label={`Remove ${item.title} from your enquiry`}
                className="relative flex size-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 max-md:before:absolute max-md:before:top-1/2 max-md:before:left-1/2 max-md:before:size-11 max-md:before:-translate-x-1/2 max-md:before:-translate-y-1/2 max-md:before:content-['']"
            >
                <CloseIcon className="size-3 fill-current" />
            </button>
        </li>
    );
};

/**
 * The shared "cart" list of bulk-enquiry selections: a scrollable list of
 * thumbnail + title + supplier rows, each removable, with an optional
 * clear-all footer. Rendered both in the island hover/tap preview (flat) and
 * in the enquiry modal's review step (grouped by supplier). Consumers supply
 * heading + padding.
 */
export const SelectedItemsList = ({
    items,
    onRemove,
    onClearAll,
    groupBySupplier = false,
    selectable = false,
    readOnly = false,
    className,
    scrollClassName,
}: SelectedItemsListProps) => {
    // Insertion-ordered groups, so the list doesn't reshuffle as items are added.
    const groups = useMemo<SupplierGroup[]>(() => {
        const byProfessional = new Map<number, SupplierGroup>();
        for (const item of items) {
            const existing = byProfessional.get(item.targetProfessionalId);
            if (existing) {
                existing.items.push(item);
                continue;
            }
            byProfessional.set(item.targetProfessionalId, {
                targetProfessionalId: item.targetProfessionalId,
                supplierName: item.supplierName,
                items: [item],
            });
        }
        return [...byProfessional.values()];
    }, [items]);

    if (items.length === 0) {
        return (
            <p
                className={cn(
                    'py-6 text-center text-sm text-gray-500',
                    className,
                )}
            >
                Nothing in your list yet.
            </p>
        );
    }

    return (
        <div className={cn('flex flex-col', className)}>
            <div
                className={cn(
                    'scrollbar-hide max-h-[45vh] overflow-y-auto',
                    scrollClassName,
                )}
            >
                {groupBySupplier ? (
                    <ul className="divide-y divide-gray-100">
                        {groups.map((group) => (
                            <li
                                key={group.targetProfessionalId}
                                className="py-2"
                            >
                                <p
                                    className={cn(
                                        'font-semibold tracking-wide text-gray-500 uppercase',
                                        // In the gate pane the heading repeats
                                        // once per row, so uppercase turns ten
                                        // supplier names into ten shouts next to
                                        // the form's own caps labels. Sentence
                                        // case keeps the grouping, drops the
                                        // noise.
                                        (selectable || readOnly) &&
                                            'text-xs font-medium tracking-normal text-[#656565] normal-case',
                                        !selectable && !readOnly && 'text-xs',
                                    )}
                                >
                                    {group.supplierName ?? 'Professional'}
                                    {/* A per-group count only earns its place
                                        when it varies. One product each — the
                                        common case — makes every heading read
                                        "· 1 product" and says nothing. Only the
                                        gate pane drops it; the legacy review
                                        step keeps its existing wording. */}
                                    {!(selectable || readOnly) ||
                                    group.items.length > 1 ? (
                                        <span className="font-normal normal-case">
                                            {` · ${countLabel(group.items)}`}
                                        </span>
                                    ) : null}
                                </p>
                                <ul>
                                    {group.items.map((item) => (
                                        <ItemRow
                                            key={item.siteTreeID}
                                            item={item}
                                            onRemove={onRemove}
                                            showSupplier={false}
                                            selectable={selectable}
                                            readOnly={readOnly}
                                        />
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {items.map((item) => (
                            <ItemRow
                                key={item.siteTreeID}
                                item={item}
                                onRemove={onRemove}
                                showSupplier
                                selectable={selectable}
                                readOnly={readOnly}
                            />
                        ))}
                    </ul>
                )}
            </div>
            {onClearAll ? (
                <div className="flex justify-end pt-2">
                    <button
                        type="button"
                        onClick={onClearAll}
                        className="rounded px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 max-md:min-h-11"
                    >
                        Clear all
                    </button>
                </div>
            ) : null}
        </div>
    );
};
